import React, { useEffect, useState } from 'react';
import { Link, NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { navGroups } from '../../data/navigation';
import { ArrowButton } from '../ui/ArrowButton';
import { Logo } from './Logo';
import { MobileMenu } from './MobileMenu';
const languages = ['FR', 'عربي', 'EN'];
export function Header() {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lang, setLang] = useState('FR');
  const location = useLocation();
  useEffect(() => {
    setOpenGroup(null);
    setMobileOpen(false);
  }, [location.pathname]);
  return <header className="sticky top-0 z-50 w-full bg-white" data-id="element-178" data-mp="39tlzyy3gtit">
      {/* Utility bar */}
      <div className="hidden bg-ink text-[13px] text-white/70 md:block" data-id="element-179" data-mp="3ugml8x9buikp">
        <div className="mx-auto flex h-10 max-w-[1320px] items-center justify-between px-6" data-id="element-180" data-mp="3uejyqn7dv7kq">
          <div className="flex items-center gap-6" data-id="element-181" data-mp="6vexw9hzyvz6">
            <Link to="/vente-de-terrains" className="flex items-center gap-2 text-white transition-colors hover:text-leaf" data-id="element-182" data-mp="btzxq7tc7dn3">
              <span className="rounded-full bg-signal px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" data-id="element-183" data-mp="gmt0wr95qdx2">
                Nouveau
              </span>
              Vente de terrains
            </Link>
            <Link to="/la-banque/agences" className="transition-colors hover:text-white" data-id="element-184" data-mp="3k41kpgng3nuf">
              Agences
            </Link>
            <Link to="/contact" className="transition-colors hover:text-white" data-id="element-185" data-mp="3u0mndvbzsmv3">
              Contact
            </Link>
            <Link to="/faq" className="transition-colors hover:text-white" data-id="element-186" data-mp="1wu68ygt9kmc">
              FAQ
            </Link>
          </div>
          <div className="flex items-center gap-6" data-id="element-187" data-mp="199m6po1em3dx">
            <a href="tel:+22245252000" className="flex items-center gap-2 transition-colors hover:text-white" data-id="element-188" data-mp="1dbh80sjc4grp">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-189" data-mp="3abvdyd5y1fn9" />
              Service client
            </a>
            <div className="flex items-center gap-1" role="group" aria-label="Langue" data-id="element-190" data-mp="35c8sjgfyot3d">
              {languages.map(l => <button key={l} type="button" onClick={() => setLang(l)} aria-pressed={lang === l} className={`rounded-full px-2.5 py-0.5 transition-colors ${lang === l ? 'bg-white/10 text-white' : 'hover:text-white'} ${l === 'عربي' ? 'font-arabic text-[12px]' : ''}`} data-id="element-191" data-mp="3jca3jopxxgwk">
                  {l}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="relative border-b border-line" onMouseLeave={() => setOpenGroup(null)} data-id="element-192" data-mp="27x4ormcjbrwl">
        <div className="mx-auto flex h-[76px] max-w-[1320px] items-center justify-between gap-6 px-6" data-id="element-193" data-mp="22zcu4kki9rop">
          <Logo data-id="element-194" data-mp="igittdh6pha1" />

          <nav aria-label="Navigation principale" className="hidden lg:block" data-id="element-195" data-mp="ifzdu2b3h2yn">
            <ul className="flex items-center gap-1" data-id="element-196" data-mp="n7r40bqc96o9">
              <li data-id="element-197" data-mp="n9c045sawxo8">
                <RouterNavLink to="/" end className={({
                isActive
              }) => `rounded-full px-3.5 py-2 text-[15px] transition-colors ${isActive ? 'text-brand' : 'text-ink hover:text-brand'}`} data-id="element-198" data-mp="9gcle60cf7hq">
                  Accueil
                </RouterNavLink>
              </li>
              {navGroups.map(group => {
              const isOpen = openGroup === group.label;
              const isActive = location.pathname.startsWith(group.path);
              return <li key={group.label} onMouseEnter={() => setOpenGroup(group.label)} data-id="element-199" data-mp="3cwjoaaaxp0pg">
                    <button type="button" aria-expanded={isOpen} onClick={() => setOpenGroup(isOpen ? null : group.label)} className={`flex items-center gap-1 rounded-full px-3.5 py-2 text-[15px] transition-colors ${isActive || isOpen ? 'text-brand' : 'text-ink hover:text-brand'}`} data-id="element-200" data-mp="3hwfi5gpx3bra">
                      {group.label}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-201" data-mp="jz0z0mhwufxb" />
                    </button>
                  </li>;
            })}
            </ul>
          </nav>

          <div className="flex items-center gap-3" data-id="element-202" data-mp="6019lcmkpx03">
            <Link to="/devenir-client" className="hidden whitespace-nowrap text-[15px] font-medium text-ink transition-colors hover:text-brand xl:block" data-id="element-203" data-mp="61chnon1xqx0">
              Devenir client
            </Link>
            <ArrowButton to="/acces-comptes" className="hidden sm:inline-flex" data-id="element-204" data-mp="2gmbmr9m414ax">
              Accès à vos comptes
            </ArrowButton>
            <button type="button" className="flex h-11 w-11 items-center justify-center rounded-full border border-line lg:hidden" aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)} data-id="element-205" data-mp="2lkwojdh0fw1h">
              {mobileOpen ? <XIcon className="h-5 w-5" data-id="element-206" data-mp="34abm2xxhm314" /> : <MenuIcon className="h-5 w-5" data-id="element-207" data-mp="349jrnqfmmbcf" />}
            </button>
          </div>
        </div>

        {/* Mega panel */}
        <AnimatePresence data-id="element-208" data-mp="398d5xl2qm6dn">
          {openGroup && <motion.div key="mega" initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="absolute inset-x-0 top-full hidden border-b border-line bg-white shadow-[0_24px_48px_-24px_rgba(16,21,18,0.18)] lg:block" data-id="element-209" data-mp="3e89bhor325vb">
              {navGroups.filter(g => g.label === openGroup).map(group => <div key={group.label} className="mx-auto grid max-w-[1320px] grid-cols-12 gap-10 px-6 py-10" data-id="element-210" data-mp="1lqokc4rnkm0m">
                    <div className="col-span-4 border-r border-line pr-10" data-id="element-211" data-mp="1gw156wijh3yp">
                      <p className="font-display text-4xl text-ink" data-id="element-212" data-mp="1urmsmz94jfp0">{group.label}</p>
                      <p className="mt-3 text-[15px] leading-relaxed text-ink-muted" data-id="element-213" data-mp="1pttmg67tmloq">{group.intro}</p>
                      <Link to={group.path} className="mt-6 inline-block text-[15px] font-medium text-brand underline-offset-4 hover:underline" data-id="element-214" data-mp="24ips5wmenmm6">
                        Voir tout l’univers
                      </Link>
                    </div>
                    <ul className="col-span-8 grid grid-cols-2 gap-x-10 gap-y-1" data-id="element-215" data-mp="29k6odety2kwf">
                      {group.links.map(link => <li key={link.path} data-id="element-216" data-mp="2x5h2j0qjrsyb">
                          <Link to={link.path} className="group block rounded-xl px-4 py-3 transition-colors hover:bg-canvas" data-id="element-217" data-mp="2x57cxe9eznt5">
                            <span className="block text-[15px] font-medium text-ink group-hover:text-brand" data-id="element-218" data-mp="2o37vikuhmhpo">
                              {link.label}
                            </span>
                            <span className="mt-0.5 block text-[13px] text-ink-muted" data-id="element-219" data-mp="2e8fxjfwztsud">{link.description}</span>
                          </Link>
                        </li>)}
                    </ul>
                  </div>)}
            </motion.div>}
        </AnimatePresence>
      </div>

      <MobileMenu open={mobileOpen} data-id="element-220" data-mp="1qjmxb79c8yty" />
    </header>;
}